import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DatePicker, Empty, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Responsive as ResponsiveGrid } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { executeReportFunction } from '../../../actions/reportingActions';
import ReportingChartCard from './ReportingChartCard';
import ReportingTextBlockSection from './ReportingTextBlockSection';
import { buildChartExecuteFilter, buildChartFilterSignature, computeChartTotal, enrichVizSpec } from './reportingChartUtils';
import { isStatVizType } from './reportingStatCard';
import {
    parseLayoutItems,
    isTextLayoutItem,
    isChartLayoutItem,
    toGridLayoutItem,
    mergeGridLayoutIntoItems,
    gridLayoutsEqual,
    layoutItemsEqual,
    gridLayoutDragProps,
} from './dashboardLayoutItems';
import {
    processChartDataset,
    pickDatasetFromResult,
    sharedChartResultCache,
    clearSharedChartResultCache,
    invalidateChartCacheForCharts,
    runWithConcurrencyPool,
    chartExecuteCacheKey,
    reprocessCachedChartResults,
    CHART_FILTER_DEBOUNCE_MS,
    CHART_EXPAND_DEBOUNCE_MS,
} from './reportingChartCache';
import useCategoryLabelMaps from './useCategoryLabelMaps';
import { resolveChartFunctionId } from './dashboardPortability';

const { Text } = Typography;
const COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const GRID_ROW_HEIGHT = 30;
const GRID_MARGIN_Y = 8;

function estimateGridMinHeight(layoutItems) {
    if (!layoutItems?.length) return 0;
    const maxRow = layoutItems.reduce((max, item) => {
        const y = item.layout?.y ?? 0;
        const h = item.layout?.h ?? 10;
        return Math.max(max, y + h);
    }, 0);
    return maxRow * GRID_ROW_HEIGHT + Math.max(0, layoutItems.length - 1) * GRID_MARGIN_Y + 16;
}

function isChartDataResolved(chartId, chartResults, chartErrors) {
    return Object.prototype.hasOwnProperty.call(chartResults, chartId) || Boolean(chartErrors[chartId]);
}

/**
 * Dashboard layout: text sections + charts in one grid.
 */
export default function DashboardChartsView({
    dashboard,
    dateRange,
    onDateRangeChange,
    showDatePicker = true,
    globalFilters = {},
    /** When false, charts are not fetched (collapse closed). */
    active = true,
    /** Increment from parent to force reload (bypasses shared result cache for this section). */
    refreshKey = 0,
    /** Allow drag/resize when onLayoutDraftChange is set. */
    editableLayout,
    /** Parent stores layout drafts; use Save changes on the panel to persist. */
    onLayoutDraftChange,
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const functions = useSelector(s => s.reportFunctionList?.items ?? []);
    const categoryLabelMaps = useCategoryLabelMaps();
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(800);
    const layoutEditable = editableLayout ?? Boolean(onLayoutDraftChange);
    const [localItems, setLocalItems] = useState([]);
    const localItemsRef = useRef([]);
    const skipLayoutDraftRef = useRef(true);
    const onLayoutDraftChangeRef = useRef(onLayoutDraftChange);
    useEffect(() => {
        onLayoutDraftChangeRef.current = onLayoutDraftChange;
    }, [onLayoutDraftChange]);

    const staticLayoutItems = useMemo(
        () => parseLayoutItems(dashboard?.layoutJson),
        [dashboard?.layoutJson],
    );
    const layoutItems = layoutEditable ? localItems : staticLayoutItems;
    const chartItems = useMemo(
        () => layoutItems.filter(isChartLayoutItem),
        [layoutItems],
    );

    useEffect(() => {
        const items = parseLayoutItems(dashboard?.layoutJson);
        setLocalItems(items);
        localItemsRef.current = items;
        skipLayoutDraftRef.current = true;
        const timer = setTimeout(() => {
            skipLayoutDraftRef.current = false;
        }, 150);
        return () => clearTimeout(timer);
    }, [dashboard?.id]);

    const handleLayoutChange = useCallback((newLayout) => {
        const currentGrid = localItemsRef.current.map(toGridLayoutItem);
        if (gridLayoutsEqual(newLayout, currentGrid)) return;

        const nextItems = mergeGridLayoutIntoItems(localItemsRef.current, newLayout);
        if (layoutItemsEqual(nextItems, localItemsRef.current)) return;

        localItemsRef.current = nextItems;
        setLocalItems(nextItems);
        if (skipLayoutDraftRef.current || !onLayoutDraftChangeRef.current) return;
        onLayoutDraftChangeRef.current(nextItems);
    }, []);

    const [chartResults, setChartResults] = useState({});
    const [chartErrors, setChartErrors] = useState({});

    const dateRangeRef = useRef(dateRange);
    const globalFiltersRef = useRef(globalFilters);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        let frameId = null;
        const ro = new ResizeObserver(entries => {
            if (frameId != null) cancelAnimationFrame(frameId);
            frameId = requestAnimationFrame(() => {
                frameId = null;
                for (const e of entries) {
                    const w = Math.round(e.contentRect.width);
                    setContainerWidth(prev => (prev === w ? prev : w));
                }
            });
        });
        ro.observe(el);
        return () => {
            if (frameId != null) cancelAnimationFrame(frameId);
            ro.disconnect();
        };
    }, []);

    useEffect(() => { dateRangeRef.current = dateRange; }, [dateRange]);
    useEffect(() => { globalFiltersRef.current = globalFilters; }, [globalFilters]);

    const filterSignature = useMemo(
        () => buildChartFilterSignature(dateRange, globalFilters),
        [dateRange, globalFilters],
    );

    const chartConfigSignature = useMemo(
        () => JSON.stringify(chartItems.map(c => ({
            id: c.id,
            functionId: c.functionId,
            dimensionFilter: c.dimensionFilter ?? null,
            datasetKey: c.datasetKey ?? 'default',
        }))),
        [chartItems],
    );

    const estimatedMinHeight = useMemo(
        () => estimateGridMinHeight(layoutItems),
        [layoutItems],
    );

    const [debouncedSig, setDebouncedSig] = useState(filterSignature);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSig(filterSignature), CHART_FILTER_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [filterSignature]);

    const [fetchActive, setFetchActive] = useState(false);
    useEffect(() => {
        if (!active) {
            setFetchActive(false);
            return undefined;
        }
        const timer = setTimeout(() => setFetchActive(true), CHART_EXPAND_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [active]);

    const clearChartState = useCallback(() => {
        setChartResults({});
        setChartErrors({});
    }, []);

    const applyResultToChart = useCallback((chartDef, rawDataset) => {
        const dataset = processChartDataset(chartDef, rawDataset, categoryLabelMaps);
        setChartResults(s => ({ ...s, [chartDef.id]: dataset }));
        setChartErrors(s => ({ ...s, [chartDef.id]: null }));
    }, [categoryLabelMaps]);

    useEffect(() => {
        if (!fetchActive) return;
        setChartResults(prev => {
            const reprocessed = reprocessCachedChartResults(chartItems, debouncedSig, categoryLabelMaps);
            if (Object.keys(reprocessed).length === 0) return prev;
            return { ...prev, ...reprocessed };
        });
    }, [categoryLabelMaps, fetchActive, chartItems, debouncedSig]);

    const reloadAllCharts = useCallback(async () => {
        const list = chartItems;
        if (list.length === 0) {
            clearChartState();
            return;
        }

        clearChartState();

        const filter = buildChartExecuteFilter({}, dateRangeRef.current, globalFiltersRef.current);
        const filterKey = buildChartFilterSignature(dateRangeRef.current, globalFiltersRef.current);

        const uniqueByFunction = new Map();
        for (const chart of list) {
            const functionId = resolveChartFunctionId(chart, functions, dashboard?.name);
            if (!functionId) continue;
            const key = chartExecuteCacheKey(functionId, filterKey);
            if (!uniqueByFunction.has(key)) {
                uniqueByFunction.set(key, { functionId, charts: [] });
            }
            uniqueByFunction.get(key).charts.push(chart);
        }

        const executeGroup = async (functionId, chartsForFn) => {
            const cacheKey = chartExecuteCacheKey(functionId, filterKey);
            let rawResult = sharedChartResultCache.get(cacheKey);

            if (!rawResult) {
                const result = await dispatch(executeReportFunction(functionId, filter));
                if (result?.error) {
                    for (const chart of chartsForFn) {
                        setChartErrors(s => ({ ...s, [chart.id]: result.error }));
                        setChartResults(s => ({ ...s, [chart.id]: null }));
                    }
                    return;
                }
                rawResult = result;
                sharedChartResultCache.set(cacheKey, rawResult);
            }

            for (const chart of chartsForFn) {
                const rawDataset = pickDatasetFromResult(rawResult, chart.datasetKey);
                if (rawDataset) {
                    applyResultToChart(chart, rawDataset);
                } else {
                    setChartErrors(s => ({ ...s, [chart.id]: t('Error loading chart') }));
                    setChartResults(s => ({ ...s, [chart.id]: null }));
                }
            }
        };

        const tasks = [...uniqueByFunction.values()].map(
            ({ functionId, charts: chartsForFn }) => () => executeGroup(functionId, chartsForFn),
        );
        await runWithConcurrencyPool(tasks);
    }, [chartItems, clearChartState, dispatch, functions, t, applyResultToChart]);

    const prevDebouncedSigRef = useRef(debouncedSig);
    useEffect(() => {
        if (prevDebouncedSigRef.current !== debouncedSig) {
            clearSharedChartResultCache();
            prevDebouncedSigRef.current = debouncedSig;
        }
    }, [debouncedSig]);

    useEffect(() => {
        if (!fetchActive) return undefined;
        reloadAllCharts();
        return undefined;
    }, [dashboard?.id, chartConfigSignature, debouncedSig, fetchActive, reloadAllCharts]);

    const lastRefreshKeyRef = useRef(0);
    useEffect(() => {
        if (!refreshKey || refreshKey === lastRefreshKeyRef.current) return;
        if (!fetchActive) return;
        lastRefreshKeyRef.current = refreshKey;
        invalidateChartCacheForCharts(chartItems, debouncedSig);
        reloadAllCharts();
    }, [refreshKey, fetchActive, chartItems, debouncedSig, reloadAllCharts]);

    if (!dashboard) return null;

    if (!active) {
        return (
            <div ref={containerRef} style={{ minHeight: 8 }} aria-hidden />
        );
    }

    if (!fetchActive) {
        return (
            <div
                ref={containerRef}
                style={{ minHeight: estimatedMinHeight || 120 }}
                aria-busy="true"
            />
        );
    }

    const gridLayout = layoutItems.map(toGridLayoutItem);

    return (
        <div
            ref={containerRef}
            style={{ minHeight: estimatedMinHeight || undefined }}
        >
            {showDatePicker && onDateRangeChange && (
                <Space style={{ marginBottom: 12 }} wrap>
                    <DatePicker.RangePicker
                        value={dateRange}
                        onChange={onDateRangeChange}
                        allowClear
                        placeholder={[t('From'), t('To')]}
                        showTime={{ format: 'HH:mm' }}
                        format="YYYY-MM-DD HH:mm"
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {t('Default: last 3 months when empty')}
                    </Text>
                </Space>
            )}

            {layoutItems.length === 0 ? (
                <Empty
                    description={t('No charts on this dashboard. Click Edit to add charts.')}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            ) : (
                <ResponsiveGrid
                    className="layout"
                    width={Math.max(containerWidth, 320)}
                    layouts={{ lg: gridLayout }}
                    cols={COLS}
                    rowHeight={30}
                    {...gridLayoutDragProps(layoutEditable)}
                    onLayoutChange={layoutEditable ? handleLayoutChange : undefined}
                    margin={[8, 8]}
                    containerPadding={[0, 0]}
                >
                    {layoutItems.map(item => {
                        if (isTextLayoutItem(item)) {
                            return (
                                <div key={item.id}>
                                    <ReportingTextBlockSection
                                        item={item}
                                        dragHandleClassName={layoutEditable ? 'drag-handle' : undefined}
                                    />
                                </div>
                            );
                        }
                        const loading = item.functionId
                            && !isChartDataResolved(item.id, chartResults, chartErrors);
                        const dataset = loading ? null : chartResults[item.id];
                        const vizSpec = enrichVizSpec(item.viz, item.dimensionFilter);
                        const total = dataset && !isStatVizType(vizSpec)
                            ? computeChartTotal(dataset, vizSpec)
                            : null;
                        return (
                            <div key={item.id}>
                                <ReportingChartCard
                                    title={item.title || t('Chart')}
                                    description={item.description}
                                    dragHandleClassName={layoutEditable ? 'drag-handle' : undefined}
                                    extra={total != null ? (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {t('Total')}: {total.toLocaleString()}
                                        </Text>
                                    ) : null}
                                    chartProps={{
                                        dataset,
                                        vizSpec,
                                        title: item.title,
                                        defaultMode: item.viz?.mode,
                                        loading,
                                        error: chartErrors[item.id],
                                        remountKey: debouncedSig,
                                    }}
                                />
                            </div>
                        );
                    })}
                </ResponsiveGrid>
            )}
        </div>
    );
}
